import { usePage } from '@inertiajs/react';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

interface FlashMessages {
    success?: string | null;
    error?: string | null;
    warning?: string | null;
    info?: string | null;
    warnings?: string[] | null;
}

interface PageProps {
    flash: FlashMessages;
    [key: string]: unknown;
}

export function useFlashMessages() {
    const { flash } = usePage<PageProps>().props;
    const shownRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (!flash) return;

        const toastKey = JSON.stringify(flash);

        // Prevent showing the same toast multiple times
        if (shownRef.current.has(toastKey)) {
            return;
        }

        if (flash.success) {
            toast.success(flash.success, { duration: 5000 });
            shownRef.current.add(toastKey);
        }

        if (flash.error) {
            toast.error(flash.error, { duration: 7000 });
            shownRef.current.add(toastKey);
        }

        if (flash.warning) {
            toast.warning(flash.warning, { duration: 6000 });
            shownRef.current.add(toastKey);
        }

        if (flash.info) {
            toast.info(flash.info, { duration: 5000 });
            shownRef.current.add(toastKey);
        }

        // Handle array of warnings
        if (flash.warnings && Array.isArray(flash.warnings)) {
            flash.warnings.forEach((warning) => {
                toast.warning(warning, { duration: 6000 });
            });
            shownRef.current.add(toastKey);
        }

        // Clean up old entries after some time to prevent memory issues
        if (shownRef.current.size > 100) {
            shownRef.current.clear();
        }
    }, [flash]);
}
